import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { apiRequest } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import {
  ArrowLeft,
  Loader2,
  MapPin,
  Calendar,
  Clock,
  Repeat,
  Users,
  Megaphone,
} from 'lucide-react'
import RsvpButton from '../../../components/events/RsvpButton'
import AttendeeList from '../../../components/events/AttendeeList'

export default function DonorEventDetail() {
  const { parishId, eventId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [event, setEvent] = useState(null)
  const [occurrences, setOccurrences] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const [rsvpData, setRsvpData] = useState({}) // occurrenceId -> { count, currentUserRsvped, rsvps }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchEvent = async () => {
    try {
      setLoading(true)
      setError(null)
      const eventData = await apiRequest(
        `/api/v1/parishes/${parishId}/events/${eventId}`,
      )
      setEvent(eventData)
    } catch (err) {
      console.error('Failed to fetch event:', err)
      setError(err.message || 'Failed to load event')
    } finally {
      setLoading(false)
    }
  }

  const fetchOccurrences = async () => {
    try {
      const data = await apiRequest(
        `/api/v1/parishes/${parishId}/events/${eventId}/occurrences?limit=20`,
      )
      setOccurrences(data.data || data || [])
    } catch (err) {
      console.error('Failed to fetch occurrences:', err)
    }
  }

  const fetchAnnouncements = async () => {
    try {
      const data = await apiRequest(
        `/api/v1/parishes/${parishId}/events/${eventId}/announcements`,
      )
      const published = (data.data || data || []).filter((a) => a.isPublished)
      setAnnouncements(published)
    } catch (err) {
      console.error('Failed to fetch announcements:', err)
    }
  }

  const fetchRsvps = async (occurrenceId) => {
    try {
      const data = await apiRequest(
        `/api/v1/parishes/${parishId}/events/${eventId}/occurrences/${occurrenceId}/rsvps`,
      )
      setRsvpData((prev) => ({
        ...prev,
        [occurrenceId]: data,
      }))
    } catch (err) {
      console.error('Failed to fetch RSVPs:', err)
    }
  }

  useEffect(() => {
    if (parishId && eventId) {
      fetchEvent()
      fetchOccurrences()
      fetchAnnouncements()
    }
  }, [parishId, eventId])

  // Fetch RSVPs for each occurrence
  useEffect(() => {
    occurrences.forEach((occ) => {
      fetchRsvps(occ.id)
    })
  }, [occurrences])

  const handleToggleRsvp = async (occurrenceId) => {
    await apiRequest(
      `/api/v1/parishes/${parishId}/events/${eventId}/occurrences/${occurrenceId}/rsvp`,
      { method: 'POST' },
    )
    // Refetch RSVPs for this occurrence
    await fetchRsvps(occurrenceId)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(`/app/donor/parish/${parishId}/events`)}
          className="text-charcoal hover:text-charcoal-muted mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      ) : !event ? (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <p className="text-charcoal-muted">Event not found.</p>
        </div>
      ) : (
        <>
          {/* Cover Image */}
          {event.imageUrl && (
            <div className="relative w-full h-64 md:h-96 rounded overflow-hidden">
              <img
                src={event.imageUrl}
                alt={event.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-background-dark/50 to-transparent" />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Event Info */}
              <div className="bg-white border border-surface-border rounded p-6">
                <h1 className="text-2xl font-bold font-display text-charcoal mb-3">
                  {event.title}
                </h1>

                {event.recurrenceRule && (
                  <div className="flex items-center gap-2 mb-3">
                    <Repeat className="w-4 h-4 text-gold" />
                    <span className="text-sm text-gold font-semibold">
                      Recurring Event
                    </span>
                  </div>
                )}

                <p className="text-base text-charcoal/90 leading-relaxed whitespace-pre-wrap">
                  {event.description}
                </p>

                <div className="mt-4 pt-4 border-t border-surface-border space-y-3">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-charcoal-muted mt-0.5" />
                    <div>
                      <p className="text-charcoal">{formatDate(event.startTime)}</p>
                      <p className="text-sm text-charcoal-muted">
                        {formatTime(event.startTime)} - {formatTime(event.endTime)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-charcoal-muted mt-0.5" />
                    <div>
                      <p className="text-charcoal">{event.locationName}</p>
                      <p className="text-sm text-charcoal-muted">
                        {event.locationAddress}
                      </p>
                    </div>
                  </div>

                  {event.host && (
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-charcoal-muted" />
                      <p className="text-charcoal">
                        Hosted by {event.host.name || event.host.email}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Upcoming Occurrences */}
              <div className="bg-white border border-surface-border rounded p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-gold" />
                  <h3 className="text-xl font-semibold font-display text-charcoal">
                    Upcoming Dates
                  </h3>
                </div>

                {occurrences.length === 0 ? (
                  <p className="text-charcoal-muted text-sm py-4 text-center">
                    No upcoming dates scheduled.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {occurrences.map((occ) => {
                      const rd = rsvpData[occ.id]
                      return (
                        <div
                          key={occ.id}
                          className="flex items-center justify-between py-3 px-4 rounded bg-surface-linen/50 border border-surface-border"
                        >
                          <div>
                            <p className="text-sm font-medium text-charcoal">
                              {formatDateTime(occ.startTime)}
                            </p>
                            <p className="text-xs text-charcoal-muted">
                              to {formatTime(occ.endTime)}
                            </p>
                          </div>
                          <RsvpButton
                            isRsvped={
                              rd?.currentUserRsvped ?? occ.currentUserRsvped ?? false
                            }
                            count={rd?.count ?? occ.rsvpCount ?? 0}
                            onToggle={() => handleToggleRsvp(occ.id)}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Announcements */}
              {announcements.length > 0 && (
                <div className="bg-white border border-surface-border rounded p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Megaphone className="w-5 h-5 text-gold" />
                    <h3 className="text-xl font-semibold font-display text-charcoal">
                      Announcements
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {announcements.map((ann, index) => (
                      <div
                        key={ann.id}
                        className={
                          index < announcements.length - 1
                            ? 'pb-4 border-b border-surface-border'
                            : ''
                        }
                      >
                        <h4 className="text-base font-semibold text-charcoal">
                          {ann.title}
                        </h4>
                        <p className="text-sm text-charcoal/90 leading-relaxed mt-1 whitespace-pre-wrap">
                          {ann.content}
                        </p>
                        {ann.publishedAt && (
                          <p className="text-xs text-charcoal-muted mt-2">
                            {new Date(ann.publishedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Attendees (if visibility is public) */}
              {event.rsvpVisibility === 'public' && occurrences.length > 0 && (
                <div className="bg-white border border-surface-border rounded p-6">
                  <h3 className="text-lg font-semibold font-display text-charcoal mb-4">
                    Attendees
                  </h3>
                  {occurrences.map((occ) => {
                    const rd = rsvpData[occ.id]
                    if (!rd?.rsvps || rd.rsvps.length === 0) return null
                    return (
                      <div key={occ.id} className="mb-4 last:mb-0">
                        <p className="text-xs text-charcoal-muted mb-2">
                          {formatDateTime(occ.startTime)}
                        </p>
                        <AttendeeList rsvps={rd.rsvps} />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
